import {
  AnchorButton,
  Classes,
  FormGroup,
  InputGroup,
  Spinner,
  SpinnerSize,
} from '@blueprintjs/core';
import * as React from 'react';
import { Tooltip } from 'vet-bones/components';
import { getIntentClassName } from 'vet-bones/utils';

import { TEST_IDS } from 'src/constants/testIds';
import { isConnectingSelector } from 'src/selectors/isConnecting';
import { Connection } from 'src/store/connections/types';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

type SignInFormProps = {
  connection: Connection;
  onSubmit: (connection: Connection) => void;
};

/**
 * Asks the user for a password for a given `Connection`;
 * calls `onSubmit` with a new Connection with password filled out
 */
export const SignInForm: React.VFC<SignInFormProps> = ({
  connection,
  onSubmit,
}) => {
  const { username } = connection;
  const isConnecting = useReduxSelector(isConnectingSelector);

  const [password, setPassword] = React.useState<string>('');

  const handleSubmit = (evt: React.FormEvent) => {
    evt.preventDefault();

    if (!password) {
      return;
    }

    const stardogConnection: Connection = { ...connection, password };
    onSubmit(stardogConnection);
  };

  return (
    <div className="sd-sign-in">
      <form className="sd-connection-form" onSubmit={handleSubmit}>
        <div className="sd-connection-form__body">
          <div
            className="sd-sign-in__title"
            data-testid={TEST_IDS.connection.form.title}
          >
            <div className={Classes.TEXT_LARGE}>
              <b>{connection.name}</b>
            </div>
            <div>{connection.endpoint}</div>
          </div>

          <FormGroup
            disabled
            label={copy.components.signInDialog.form.username}
            labelFor="sd-connection-username"
          >
            <InputGroup
              autoComplete="username"
              disabled
              id="sd-connection-username"
              name="username"
              value={username}
            />
          </FormGroup>
          <FormGroup
            label={copy.components.signInDialog.form.password}
            labelFor="sd-connection-password"
          >
            <InputGroup
              autoComplete="password"
              autoFocus
              data-testid={TEST_IDS.connection.form.password}
              id="sd-connection-password"
              name="password"
              onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                setPassword(evt.target.value);
              }}
              type="password"
              value={password}
            />
          </FormGroup>
          <input hidden type="submit" />
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Tooltip
              content={copy.components.signInDialog.form.passwordRequired}
              disabled={Boolean(password)}
            >
              <AnchorButton
                className={getIntentClassName('sd-green')}
                data-testid={TEST_IDS.connection.form.submitButton}
                disabled={!password}
                icon={
                  isConnecting ? (
                    <Spinner size={SpinnerSize.SMALL} />
                  ) : undefined
                }
                onClick={handleSubmit}
                text={copy.components.signInDialog.submit}
              />
            </Tooltip>
          </div>
        </div>
      </form>
    </div>
  );
};
